import { useEffect, lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Services from "@/components/Services";
import AnimatedSection from "@/components/AnimatedSection";
import { useSeo } from "@/hooks/useSeo";
import { useT } from "@/i18n";

const WhyChooseUs = lazy(() => import("@/components/WhyChooseUs"));
const ServiceAreas = lazy(() => import("@/components/ServiceAreas"));
const BeforeAfter = lazy(() => import("@/components/BeforeAfter"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const Pricing = lazy(() => import("@/components/Pricing"));
const ContactForm = lazy(() => import("@/components/ContactForm"));
const Footer = lazy(() => import("@/components/Footer"));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
const FloatingContact = lazy(() => import("@/components/FloatingContact"));

const Index = () => {
  const location = useLocation();
  const t = useT();

  useSeo({
    title: t(
      "Nettoyage Boudreau | Bacs & haute pression — Valleyfield, Beauharnois, Soulanges, Haut-Saint-Laurent",
      "Bin Cleaning & Pressure Washing | Valleyfield, Beauharnois, Soulanges & Haut-Saint-Laurent",
    ),
    description: t(
      "Nettoyage professionnel de bacs à ordures, recyclage et compost à Valleyfield, Beauharnois, Soulanges et Haut-Saint-Laurent à partir de 20$/bac. 450-802-9003",
      "Professional garbage, recycling and compost bin cleaning plus pressure washing in Valleyfield, Beauharnois, Soulanges and Haut-Saint-Laurent. From $20 per bin. Call 450-802-9003.",
    ),
    canonical: "https://nettoyageboudreau.com/",
  });

  useEffect(() => {
    const state = location.state as { scrollTo?: string } | null;
    if (state?.scrollTo) {
      // Wait a tick for sections to render
      setTimeout(() => {
        const el = document.getElementById(state.scrollTo!);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [location.state]);

  // JSON-LD complet (LocalBusiness, Reviews, Services, Offers, etc.)
  // est déjà injecté en statique dans index.html — on ne duplique pas ici.
  return (
    <main className="min-h-screen">
      <Header />
      <Hero />
      <AnimatedSection animation="fade-up">
        <Services />
      </AnimatedSection>
      <Suspense fallback={null}>
        <AnimatedSection animation="fade-up" delay={100} className="content-visibility-auto">
          <Pricing />
        </AnimatedSection>
        <AnimatedSection animation="fade-up" className="content-visibility-auto">
          <ServiceAreas />
        </AnimatedSection>
        <AnimatedSection animation="fade-up" className="content-visibility-auto">
          <ContactForm />
        </AnimatedSection>
        <AnimatedSection animation="fade-up" delay={100} className="content-visibility-auto">
          <WhyChooseUs />
        </AnimatedSection>
        <AnimatedSection animation="fade-up" delay={100} className="content-visibility-auto">
          <BeforeAfter />
        </AnimatedSection>
        <AnimatedSection animation="fade-up" className="content-visibility-auto">
          <Testimonials />
        </AnimatedSection>
        <Footer />
        <CookieConsent />
        <FloatingContact />
      </Suspense>
    </main>
  );
};

export default Index;
